<template>
  <nuxt-link
    v-if="href"
    :to="href"
    class="whitespace-nowrap font-semibold shadow-sm"
    :class="[
      sizeClasses,
      typeClasses,
      roundedClass,
      !disabled && !loading ? hoverClass : '',
      {
        'cursor-not-allowed opacity-60': disabled,
        relative: count,
        'whitespace-nowrap': hasNoWrap,
      },
    ]"
    :target="target"
  >
    <slot />
  </nuxt-link>
  <button
    v-else
    class="shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
    :class="[
      sizeClasses,
      typeClasses,
      roundedClass,
      !disabled && !loading ? hoverClass : '',
      isTextBold ? 'font-semibold' : 'font-normal',
      {
        'relative inline-flex cursor-progress items-center justify-center !text-transparent':
          loading,
        relative: count,
        'whitespace-nowrap': hasNoWrap,
      },
    ]"
    type="button"
    :disabled="disabled"
  >
    <slot v-if="!(loading && iconOnly)" />
    <Spinner
      v-if="loading"
      :class="{
        absolute: !iconOnly,
      }"
      :colored="['secondary', 'soft'].includes(type)"
    />
    <CountBadge
      v-if="count"
      :count="count"
      :hide-count-number="hideCountNumber"
    />
  </button>
</template>

<script lang="ts">
type ButtonSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl';

type ButtonType =
  | 'primary'
  | 'secondary'
  | 'soft'
  | 'danger'
  | 'danger-soft'
  | 'success'
  | 'outline'
  | 'outline-active';

type ButtonTarget = '_blank' | '_parent' | '_self' | '_top' | null | undefined;

export type ButtonRoundedType =
  | 'left'
  | 'right'
  | 'none'
  | 'full'
  | 'bottom'
  | 'bottom-left'
  | 'bottom-right';

export type ButtonRoundedSize = 'md' | 'lg';

export default {
  props: {
    href: {
      type: String,
      default: null,
    },
    size: {
      type: String as PropType<ButtonSize>,
      default: 'base' as ButtonSize,
    },
    type: {
      type: String as PropType<ButtonType>,
      default: 'primary' as ButtonType,
    },
    onDarkBg: {
      type: Boolean,
      default: false,
    },
    roundType: {
      type: String as PropType<ButtonRoundedType>,
      default: '',
    },
    roundSize: {
      type: String as PropType<ButtonRoundedSize>,
      default: '',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    count: {
      type: Number,
      default: undefined,
    },
    hideCountNumber: {
      type: Boolean,
      default: false,
    },
    target: {
      type: String as PropType<ButtonTarget>,
    },
    isTextBold: {
      type: Boolean,
      default: true,
    },
    hasNoWrap: {
      type: Boolean,
      default: true,
    },
  },

  computed: {
    sizeClasses() {
      if (this.iconOnly) {
        let size;
        switch (this.size) {
          case 'lg':
            size = 'w-9 h-9';
            break;
          default:
            size = 'w-8 h-8';
        }
        return size + ' text-sm inline-flex justify-center items-center';
      }
      switch (this.size) {
        case 'xs':
          return 'px-2 py-1 text-xs';
        case 'sm':
          return 'px-2 py-1 text-sm';
        case 'base':
          return 'px-2.5 py-1.5 text-sm';
        case 'lg':
          return 'px-3 py-2 text-sm';
        case 'xl':
          return 'px-3.5 py-2.5 text-sm';
        default:
          return '';
      }
    },
    typeClasses() {
      switch (this.type) {
        case 'primary': {
          const append = this.onDarkBg
            ? 'bg-indigo-500 focus-visible:outline-indigo-500'
            : 'bg-indigo-600 focus-visible:outline-indigo-600';
          return (
            append +
            ' text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2'
          );
        }
        case 'secondary':
          return this.onDarkBg
            ? 'bg-white/10 text-white'
            : 'bg-white text-gray-900 ring-1 ring-inset ring-gray-300';
        case 'soft':
          return this.onDarkBg
            ? 'bg-indigo-600/30 text-indigo-300'
            : 'bg-indigo-50 text-indigo-600';
        case 'danger':
          return 'bg-red-600 text-white';
        case 'danger-soft':
          return 'bg-red-200 text-red-900';
        case 'success':
          return 'bg-green-600 text-white';
        case 'outline-active':
          return 'bg-indigo-600 text-white font-semibold border border-indigo-600';
        case 'outline':
          return 'bg-transparent text-indigo-700 font-semibold border border-indigo-600';
        default:
          return '';
      }
    },
    hoverClass() {
      switch (this.type) {
        case 'primary': {
          return this.onDarkBg ? 'hover:bg-indigo-400' : 'hover:bg-indigo-500';
        }
        case 'secondary':
          return this.onDarkBg
            ? 'hover:bg-white/20'
            : 'hover:bg-gray-50 dark:hover:bg-gray-100';
        case 'soft':
          return this.onDarkBg
            ? 'hover:bg-indigo-600/60'
            : 'hover:bg-indigo-100';
        case 'danger':
          return 'hover:bg-red-500';
        case 'danger-soft':
          return 'hover:bg-red-300';
        case 'success':
          return 'hover:bg-green-700';
        case 'outline-active':
          return 'hover:bg-indigo-600 hover:cursor-default hover:text-white hover:border-transparent';
        case 'outline':
          return 'hover:bg-indigo-100';

        default:
          return '';
      }
    },
    roundedClass() {
      let edge = '';

      switch (this.roundType) {
        case 'none':
          return '';
        case 'full':
          return 'rounded-full';
        case 'left':
          edge = '-l';
          break;
        case 'right':
          edge = '-r';
          break;
        case 'bottom':
          edge = '-b';
          break;
        case 'bottom-left':
          edge = '-bl';
          break;
        case 'bottom-right':
          edge = '-br';
          break;
      }

      const size = this.roundSize?.length
        ? `-${this.roundSize}`
        : this.size === 'xs' || this.size === 'sm'
          ? ''
          : '-md';
      return `rounded${edge}${size}`;
    },
  },
};
</script>
